import { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
// Debug handle for the scroll audits in /app/tests (read-only; no behaviour).
if (typeof window !== "undefined") window.__gmScrollTrigger = ScrollTrigger;

// iOS Safari (client, 2026-09-17): the toolbar collapsing / expanding mid-scroll
// fires window "resize". By default ScrollTrigger answers every resize with a
// full refresh() - reverting and re-placing every trigger and restoring the
// pre-gesture scroll offset - which on a phone reads as "the page stops, jumps
// back to where I started, then scrolls again". ignoreMobileResize skips
// height-only resizes on touch devices; on phones we also stop refreshing on
// resize altogether (orientation changes still refresh via the phone/desktop
// remount + load/visibility events).
const PHONE = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
ScrollTrigger.config({
  ignoreMobileResize: true,
  ...(PHONE ? { autoRefreshEvents: "visibilitychange,DOMContentLoaded,load" } : {}),
});

// Split a single-line element into wrapped words for masked line reveal.
// Non-invasive: only touches immediate text children.
function wrapWordsIntoLines(el) {
  if (el.dataset.split === "1") return;
  const raw = el.textContent.trim();
  el.textContent = "";
  const words = raw.split(/\s+/);
  words.forEach((w, i) => {
    const line = document.createElement("span");
    line.className = "mask-line";
    const inner = document.createElement("span");
    inner.textContent = w;
    line.appendChild(inner);
    el.appendChild(line);
    if (i < words.length - 1) {
      el.appendChild(document.createTextNode(" "));
    }
  });
  el.dataset.split = "1";
}

// Fit-to-width: sizes a single-line element so its rendered text spans the
// container width exactly. Measures at a reference font-size (100px) and
// scales linearly. Re-runs on resize + document.fonts.ready. Returns a
// disposer. Robust to missing elements or zero-size containers (no-op).
// Explicitly load the display face used by the wordmark before measuring.
// WebKit resolves document.fonts.ready early (before faces that are only used
// by hidden/late elements have loaded), so measuring on `ready` alone yields
// fallback-font metrics and an over-sized fit. fonts.load() forces the request
// and resolves when the face is actually usable. Falls back after 2.5s.
export function whenDisplayFontReady() {
  if (!document.fonts || typeof document.fonts.load !== "function") return Promise.resolve();
  const loads = [
    document.fonts.load('900 100px "HN Display"'),
    document.fonts.load('700 100px "HN Display"'),
  ];
  // Only the two display faces gate the reveal. (Chaining document.fonts.ready
  // here made the loader wait ~2.4s in Safari - it settles every font on the
  // page, long after these two are in. Load audit, 2026-09-19.)
  const timeout = new Promise((r) => setTimeout(r, 2500));
  return Promise.race([Promise.all(loads), timeout]).catch(() => {});
}

// Fire cb every time the font set finishes loading anything (font swap).
function onFontsLoadingDone(cb) {
  if (!document.fonts || typeof document.fonts.addEventListener !== "function") return () => {};
  document.fonts.addEventListener("loadingdone", cb);
  return () => document.fonts.removeEventListener("loadingdone", cb);
}

export function fitTextToWidth(el, {
  container = null,
  minFontPx = 24,
  maxFontPx = 400,
  paddingFactor = 1.0, // 1.0 = fill; 0.96 leaves a small margin
  measureText = false, // block-level text: measure the glyph run, not the box
} = {}) {
  if (!el) return () => {};
  const box = container || el.parentElement;
  if (!box) return () => {};
  let raf = 0;
  let disposed = false;
  // Width gate (scroll audit 2026-09-18): the fit depends on the container
  // width only, but iOS Safari fires `resize` for every toolbar collapse /
  // expand mid-scroll (height only) and ResizeObserver fires for the box's
  // own height changes. Each measure below costs a forced full-page layout,
  // so skip when the available width is unchanged; font events force.
  let lastAvailW = -1;

  const textWidth = () => {
    try {
      const r = document.createRange();
      r.selectNodeContents(el);
      return r.getBoundingClientRect().width;
    } catch { return 0; }
  };

  const availWidth = () => {
    const cs = getComputedStyle(box);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    return Math.max(1, box.clientWidth - padL - padR);
  };

  const measure = (force) => {
    if (disposed) return;
    try {
      const availW = availWidth();
      if (!force && Math.round(availW) === Math.round(lastAvailW)) return;
      lastAvailW = availW;
      // Reset any inline font-size to allow natural measurement.
      const REF = 100; // px
      el.style.fontSize = `${REF}px`;
      // Force a layout read of the wrapped text width at ref size.
      // getBoundingClientRect first: WebKit returns 0/odd values for
      // scrollWidth on inline-level boxes (inline-flex lockups included).
      const naturalW = (measureText && textWidth()) || el.getBoundingClientRect().width || el.scrollWidth || 1;
      if (!isFinite(naturalW) || naturalW <= 0 || !isFinite(availW)) return;
      const targetPx = Math.max(
        minFontPx,
        Math.min(maxFontPx, (availW / naturalW) * REF * paddingFactor)
      );
      el.style.fontSize = `${targetPx}px`;
    } catch {
      // On any failure leave the element without inline font-size; CSS default applies.
      el.style.removeProperty("font-size");
    }
  };

  const schedule = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => measure(false));
  };
  const scheduleForced = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => measure(true));
  };

  // Initial: measure now, again when the display face is really loaded, and
  // on a few settle ticks (Safari swaps fonts late; metrics shift a lot).
  // These are forced - glyph metrics change while the width does not.
  scheduleForced();
  whenDisplayFontReady().then(scheduleForced);
  const offFonts = onFontsLoadingDone(scheduleForced);
  const timers = [400, 1200, 2500].map((ms) => setTimeout(scheduleForced, ms));

  // Width-gated: box height changes and iOS toolbar resizes are skipped.
  const ro = "ResizeObserver" in window ? new ResizeObserver(schedule) : null;
  ro?.observe(box);
  window.addEventListener("resize", schedule);

  return () => {
    disposed = true;
    cancelAnimationFrame(raf);
    timers.forEach(clearTimeout);
    offFonts();
    ro?.disconnect();
    window.removeEventListener("resize", schedule);
  };
}


// Public: attach reveal behavior to any element with data-reveal="mask|fade|img"
export function initReveals(scope = document) {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // A new page (or hero) is arming its reveals: the previous route's nodes are
  // detached by now, so drop their triggers before adding ours.
  killDetachedTriggers(false);

  // 1. masked headline reveals — group children into lines using natural line breaks
  scope.querySelectorAll('[data-reveal="mask"]').forEach((el) => {
    if (el.dataset.split === "1") return;
    // If author already provided .mask-line children, skip wrapping.
    const alreadyLines = el.querySelector(".mask-line");
    if (!alreadyLines) wrapWordsIntoLines(el);
    const lines = el.querySelectorAll(".mask-line > span");
    // Once revealed, the mask's overflow:hidden has done its job. Release it
    // (`is-revealed`) so the text-shadow is no longer clipped to the line box -
    // over a bright video frame that clipped shadow read as a faint input box
    // behind the hero headline (client, 2026-09-19).
    const release = () => el.querySelectorAll(".mask-line").forEach((l) => l.classList.add("is-revealed"));
    if (reduce) {
      gsap.set(lines, { y: 0, opacity: 1 });
      release();
      return;
    }
    gsap.set(lines, { y: "110%" });
    ScrollTrigger.create({
      trigger: el,
      start: "top 85%",
      once: true,
      onEnter: () => {
        gsap.to(lines, {
          y: "0%",
          duration: 0.9,
          ease: "expo.out",
          stagger: 0.06,
          onComplete: release,
        });
      },
    });
  });

  // 2. fade-up
  scope.querySelectorAll('[data-reveal="fade"]').forEach((el) => {
    if (el.dataset.faded === "1") return;
    el.classList.add("fade-up");
    el.dataset.faded = "1";
    ScrollTrigger.create({
      trigger: el,
      start: "top 88%",
      once: true,
      onEnter: () => el.classList.add("is-in"),
    });
  });

  // 3. image mask (scale 1.15 -> 1 on enter)
  scope.querySelectorAll('[data-reveal="img"]').forEach((el) => {
    if (el.dataset.imgIn === "1") return;
    el.dataset.imgIn = "1";
    el.classList.add("img-mask");
    ScrollTrigger.create({
      trigger: el,
      start: "top 90%",
      once: true,
      onEnter: () => el.classList.add("is-in"),
    });
  });

  // 4. parallax on [data-parallax-speed]
  // Phones: the CSS phone pass forces `transform: none !important` on these
  // images (whole frame, no crop), so the scrub tween had no visible effect but
  // still wrote a transform on every scroll frame. Not created there (client
  // approved, scroll audit 2026-09-18).
  const phone = window.matchMedia("(max-width: 767px)").matches;
  scope.querySelectorAll("[data-parallax-speed]").forEach((el) => {
    if (el.dataset.pxInit === "1") return;
    el.dataset.pxInit = "1";
    const speed = parseFloat(el.dataset.parallaxSpeed || "0.2");
    if (reduce || phone) return;
    gsap.fromTo(
      el,
      { yPercent: -speed * 20 },
      {
        yPercent: speed * 20,
        ease: "none",
        scrollTrigger: {
          trigger: el.closest("[data-parallax-container]") || el.parentElement,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      }
    );
  });
}

// Hero wordmark — SINGLE-ELEMENT Rennsport pattern, rendered at GIANT size.
//
// Elements:
//   placeholder — hidden fit-to-width text in the hero. Defines the giant start
//                 rect (and reserves hero content padding). Never the visible
//                 wordmark once the scrub is live.
//   sizer       — the nav's real 16px "GRID MASTERS" text. Reserves layout in
//                 the lockup and is what shows on non-home routes. Hidden on
//                 home while live/docked.
//   morph       — the ONE visible wordmark on home. Lives inside the nav lockup
//                 (absolute, bottom-left anchored to the sizer), set at the
//                 giant font-size, and is transformed FROM the placeholder rect
//                 TO the sizer rect. Because it is rasterised at full size and
//                 only ever scaled DOWN, it stays razor sharp in WebKit (Safari
//                 rasterises a layer at its natural size — scaling a 16px layer
//                 up 15x looks like a 140p video). It stays docked (scaled to
//                 nav size) as the nav logo; there is no swap and no fade.
//   The shield mark is PART of the lockup (img + text in em units), so the
//   whole lockup shrinks and travels together — nothing pops in at the end.
//
// Geometry: the scrub runs over D = placeholderBottom(at hero top) - sizerBottom
// px of scroll. The placeholder scrolls with the hero at 1px/px and the morph's
// y goes dy0 -> 0 over the same D px, so the wordmark's bottom stays glued to
// the hero's bottom padding for the whole shrink and docks exactly when the
// hero's bottom edge reaches the nav line. It never leaves the hero.
//
// body[data-wm-state]: boot | live | docked | static (see components.css).
//
// `gm:wm-ready` fires on window the first time the state leaves `boot` (the
// scrub is built, or the static fallback is in place). The page Loader holds
// the site behind the shield until then (client, 2026-10-22: "the logo
// transition only starts working after a scroll or two" - the site was being
// revealed 1-3s before the scrub existed, so the giant wordmark just scrolled
// away with the hero and the nav lockup was missing until a late rebuild).
export const WM_READY = "gm:wm-ready";
let wmReadyAnnounced = false;
const announceWmReady = () => {
  if (wmReadyAnnounced) return;
  wmReadyAnnounced = true;
  window.__gmWmReady = true;
  try { window.dispatchEvent(new CustomEvent(WM_READY)); } catch { /* ignore */ }
};

export function initHeroWordmark({ hero, placeholder, sizer, morph }) {
  if (!hero || !placeholder || !sizer || !morph) return null;
  const body = document.body;
  const setWm = (s) => {
    if (body.getAttribute("data-wm-state") !== s) body.setAttribute("data-wm-state", s);
    if (s !== "boot") announceWmReady();
  };

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduce) {
    setWm("static");
    return () => body.removeAttribute("data-wm-state");
  }

  let tween = null;
  let shieldTween = null;
  let lastKey = "";
  let disposed = false;
  const timers = [];

  const killAll = () => {
    tween?.scrollTrigger?.kill();
    tween?.kill();
    shieldTween?.scrollTrigger?.kill();
    shieldTween?.kill();
    tween = null;
    shieldTween = null;
  };

  const compute = () => {
    gsap.set(morph, { clearProps: "transform" });
    // SIZE BY CONSTRUCTION, never by measuring the morph's width (WebKit
    // under-reports the natural width of the absolutely positioned lockup,
    // which inflated every derived scale ~15%).
    //   • The morph's font-size is set to the placeholder's fitted font-size,
    //     so start scale is exactly 1 and the morph == placeholder.
    //   • End scale is the pure ratio sizerFont / F. The lockup recipe is
    //     em-based, so at that scale the morph == the nav sizer exactly.
    const F = parseFloat(getComputedStyle(placeholder).fontSize);
    const sizerF = parseFloat(getComputedStyle(sizer).fontSize);
    if (!isFinite(F) || F <= 0 || !isFinite(sizerF) || sizerF <= 0) return null;
    morph.style.fontSize = `${F}px`;

    const p = placeholder.getBoundingClientRect();
    const m = morph.getBoundingClientRect();
    const z = sizer.getBoundingClientRect();
    const h = hero.getBoundingClientRect();
    if ([p, m, z].some((r) => r.width <= 0 || r.height <= 0) || h.height <= 0) return null;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const heroTopDoc = h.top + scrollY;
    // Placeholder bottom in viewport coords when the hero top is at viewport top.
    const pBottomAtStart = (p.bottom + scrollY) - heroTopDoc;
    // START: morph sits exactly on the placeholder (positions only).
    const s0 = 1;
    const dx0 = p.left - m.left;
    const dy0 = pBottomAtStart - m.bottom;
    // END: morph sits exactly on the sizer (positions only + font ratio).
    const sE = sizerF / F;
    const dxE = z.left - m.left;
    const dyE = z.bottom - m.bottom;
    const D = pBottomAtStart - z.bottom; // scroll distance of the handoff
    const vals = [dx0, dy0, sE, dxE, dyE, D];
    if (!vals.every(isFinite) || sE <= 0 || D <= 0) return null;
    return { s0, dx0, dy0, sE, dxE, dyE, D };
  };

  const build = () => {
    if (disposed) return;
    try {
      killAll();
      gsap.set(morph, { clearProps: "transform" });
      const g = compute();
      if (!g) { setWm("static"); return; }

      gsap.set(morph, { transformOrigin: "left bottom", willChange: "transform" });
      tween = gsap.fromTo(
        morph,
        { x: g.dx0, y: g.dy0, scale: g.s0 },
        {
          x: g.dxE, y: g.dyE, scale: g.sE,
          ease: "none",
          immediateRender: true,
          scrollTrigger: {
            trigger: hero,
            start: "top top",
            end: () => `+=${Math.round(g.D)}`,
            scrub: true,
            invalidateOnRefresh: true,
            onUpdate: (self) => setWm(self.progress >= 1 ? "docked" : "live"),
            onRefresh: (self) => setWm(self.progress >= 1 ? "docked" : "live"),
          },
        }
      );
      const st = tween.scrollTrigger;
      setWm(st && st.progress >= 1 ? "docked" : "live");
    } catch (err) {
      setWm("static");
      console.warn("[initHeroWordmark] build failed:", err);
    }
  };

  const bucketKey = () => `${Math.round(window.innerWidth / 8) * 8}x${Math.round(window.innerHeight / 8) * 8}`;
  const onResize = () => {
    const k = bucketKey();
    if (k === lastKey) return;
    lastKey = k;
    build();
    ScrollTrigger.refresh();
  };

  // Signature of everything the geometry depends on. If it changes (font swap,
  // late fit-to-width, image sizing), we rebuild. Cheap, so we check it on a
  // few settle ticks after init and on every fonts "loadingdone".
  const signature = () => {
    const p = placeholder.getBoundingClientRect();
    const z = sizer.getBoundingClientRect();
    const tr = placeholder.parentElement;
    const trW = tr ? tr.clientWidth : 0;
    return [p.width, p.height, p.left, z.width, z.height, trW, window.innerWidth, window.innerHeight]
      .map((v) => Math.round(v)).join("|");
  };
  let lastSig = "";
  const rebuildIfChanged = () => {
    if (disposed) return;
    const sig = signature();
    if (sig === lastSig) return;
    lastSig = sig;
    build();
    ScrollTrigger.refresh();
  };

  const kick = () => {
    if (disposed) return;
    lastKey = bucketKey();
    lastSig = signature();
    build();
    ScrollTrigger.refresh();
    [250, 600, 1200, 2000, 3200].forEach((ms) => timers.push(setTimeout(rebuildIfChanged, ms)));
  };
  const offFonts = onFontsLoadingDone(() => requestAnimationFrame(rebuildIfChanged));

  // Re-sync whenever the placeholder's box changes (fit-to-width pass landing
  // after us, font swap, orientation change). Debounced to one build per frame.
  let roRaf = 0;
  let lastPh = "";
  const ro = "ResizeObserver" in window
    ? new ResizeObserver(() => {
        if (disposed) return;
        const r = placeholder.getBoundingClientRect();
        const key = `${Math.round(r.width)}x${Math.round(r.height)}`;
        if (key === lastPh) return;
        lastPh = key;
        cancelAnimationFrame(roRaf);
        roRaf = requestAnimationFrame(() => { build(); ScrollTrigger.refresh(); });
      })
    : null;
  ro?.observe(placeholder);

  setWm("boot");
  whenDisplayFontReady().then(() => requestAnimationFrame(kick));
  window.addEventListener("resize", onResize);
  window.addEventListener("load", build);

  return () => {
    disposed = true;
    timers.forEach(clearTimeout);
    cancelAnimationFrame(roRaf);
    ro?.disconnect();
    offFonts();
    window.removeEventListener("resize", onResize);
    window.removeEventListener("load", build);
    killAll();
    gsap.set(morph, { clearProps: "transform,willChange" });
    morph.style.removeProperty("font-size");
    body.removeAttribute("data-wm-state");
  };
}

export function useReveals(deps = []) {
  useEffect(() => {
    initReveals(document);
    // refresh after fonts/images settle
    const t1 = setTimeout(() => ScrollTrigger.refresh(), 200);
    const t2 = setTimeout(() => ScrollTrigger.refresh(), 900);
    // Phones only (client, 2026-10-10): with resize refreshes switched off above,
    // content that lands after 900ms (rounds from the API, lazy images, the hero
    // film) moved everything below it without the triggers being re-measured, so
    // late sections could sit un-revealed. Re-measure when the DOCUMENT height
    // changes (not the viewport - iOS toolbar resizes do not touch this).
    let ro = null;
    let roTimer = 0;
    let lastH = 0;
    if (PHONE && "ResizeObserver" in window) {
      ro = new ResizeObserver(() => {
        const h = Math.round(document.documentElement.scrollHeight);
        if (h === lastH) return;
        lastH = h;
        clearTimeout(roTimer);
        roTimer = setTimeout(() => ScrollTrigger.refresh(), 150);
      });
      ro.observe(document.body);
    }
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(roTimer);
      ro?.disconnect();
      // The page is going away: sweep its triggers once React has detached
      // the nodes (next frame). Without this every route left its reveal /
      // parallax triggers behind on dead elements (client, 2026-09-19).
      requestAnimationFrame(() => killDetachedTriggers(true));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Kill every ScrollTrigger (and its tween) whose trigger element is no longer
 * in the document. Reveal / parallax triggers are created per page by
 * initReveals(); React unmounts the page but GSAP kept the triggers, so after
 * a few client-side hops the landing page was being driven by a pile of dead
 * triggers from other routes - stale scrub tweens still writing transforms,
 * a second hero-wordmark scrub flipping body[data-wm-state], and every
 * refresh() re-measuring detached nodes. Symptom: "go to any microsite, come
 * back home, the Creed is broken" (client, 2026-09-19).
 */
export function killDetachedTriggers(refresh = false) {
  let killed = 0;
  ScrollTrigger.getAll().forEach((t) => {
    const el = t.trigger || (t.pin && t.pin.parentNode);
    if (el && !document.documentElement.contains(el)) {
      t.animation?.kill();
      t.kill(false);
      killed += 1;
    }
  });
  if (killed && refresh) ScrollTrigger.refresh();
  return killed;
}
